import React, { useState } from "react";
import { Button, Table, Typography } from "@/components";
import {
  Key,
  Copy,
  Trash2,
  Plus,
  BookOpen,
  Code2,
  Shield,
  BarChart3,
  ChevronDown,
  ChevronRight,
} from "lucide-react";

// --- Dummy API keys & usage data ---
const DUMMY_API_KEYS = [
  {
    id: "1",
    name: "Production",
    keyPrefix: "pk_live_••••••••••••••••a3f2",
    createdAt: "2025-01-15",
    lastUsed: "2025-01-30",
    status: "Active",
  },
  {
    id: "2",
    name: "Staging",
    keyPrefix: "pk_test_••••••••••••••••b7c1",
    createdAt: "2025-01-20",
    lastUsed: "2025-01-28",
    status: "Active",
  },
];

const DUMMY_USAGE = {
  requestsToday: 12450,
  requestsLimit: 100000,
  rateLimitRemaining: 98500,
};

const DUMMY_ENDPOINTS = [
  { method: "GET", path: "/v1/agents", description: "List all agents" },
  { method: "GET", path: "/v1/agents/:id", description: "Get agent by ID" },
  { method: "POST", path: "/v1/agents", description: "Create agents" },
  { method: "GET", path: "/v1/campaigns", description: "List campaigns" },
  { method: "POST", path: "/v1/campaigns", description: "Create campaign" },
  { method: "GET", path: "/v1/integration", description: "List integrations" },
];

export const IntegrationsDeveloper: React.FC = () => {
  const [docsSection, setDocsSection] = useState<"auth" | "base" | "endpoints" | null>("auth");
  const [showCreateKey, setShowCreateKey] = useState(false);

  const handleCopyKey = (prefix: string) => {
    navigator.clipboard.writeText(prefix);
    // In a real app you'd toast here
  };

  const apiKeyColumns = [
    { id: "name", label: "Name" },
    { id: "keyPrefix", label: "Key" },
    { id: "createdAt", label: "Created" },
    { id: "lastUsed", label: "Last used" },
    { id: "status", label: "Status" },
    {
      id: "actions",
      label: "Actions",
      format: (_: unknown, row: (typeof DUMMY_API_KEYS)[0]) => (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => handleCopyKey(row.keyPrefix)}
            aria-label="Copy key"
          >
            <Copy className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-destructive hover:text-destructive"
            aria-label="Revoke key"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  const usagePercent = Math.round(
    (DUMMY_USAGE.requestsToday / DUMMY_USAGE.requestsLimit) * 100
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-8">
      <div>
        <h1 className="text-3xl font-bold">Developer</h1>
        <p className="text-muted-foreground mt-1">
          Manage API keys and explore the API documentation.
        </p>
      </div>

      {/* API Management */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <Key className="h-5 w-5 text-primary" />
          <Typography variant="h3" className="text-xl font-semibold">
            API keys
          </Typography>
        </div>
        <div className="rounded-lg border-2 border-input bg-card shadow-sm overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-input bg-muted/30">
            <p className="text-sm text-muted-foreground">
              Use API keys to authenticate requests. Keep them secret.
            </p>
            <Button
              size="sm"
              onClick={() => setShowCreateKey(!showCreateKey)}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              Create key
            </Button>
          </div>
          {showCreateKey && (
            <div className="p-4 border-b border-input bg-muted/20">
              <p className="text-sm text-muted-foreground mb-3">
                Dummy form: key creation would happen here.
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Key name (e.g. Production)"
                  className="flex h-10 rounded-xl border-2 border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                />
                <Button size="sm" variant="secondary" onClick={() => setShowCreateKey(false)}>
                  Cancel
                </Button>
                <Button size="sm">Create</Button>
              </div>
            </div>
          )}
          <Table<{ id: string; name: string; keyPrefix: string; createdAt: string; lastUsed: string; status: string }>
            columns={apiKeyColumns}
            rows={DUMMY_API_KEYS}
            getRowId={(row) => row.id}
          />
        </div>
      </section>

      {/* Usage */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-primary" />
          <Typography variant="h3" className="text-xl font-semibold">
            Usage
          </Typography>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="rounded-lg border-2 border-input bg-card p-4 shadow-sm">
            <p className="text-sm text-muted-foreground">Requests today</p>
            <p className="text-2xl font-bold">{DUMMY_USAGE.requestsToday.toLocaleString()}</p>
          </div>
          <div className="rounded-lg border-2 border-input bg-card p-4 shadow-sm">
            <p className="text-sm text-muted-foreground">Daily limit</p>
            <p className="text-2xl font-bold">{DUMMY_USAGE.requestsLimit.toLocaleString()}</p>
          </div>
          <div className="rounded-lg border-2 border-input bg-card p-4 shadow-sm">
            <p className="text-sm text-muted-foreground">Remaining</p>
            <p className="text-2xl font-bold">{DUMMY_USAGE.rateLimitRemaining.toLocaleString()}</p>
          </div>
        </div>
        <div className="rounded-lg border-2 border-input bg-card p-4 shadow-sm">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-muted-foreground">Daily usage</span>
            <span className="font-medium">{usagePercent}%</span>
          </div>
          <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
            <div
              className="h-full rounded-full bg-primary transition-all"
              style={{ width: `${Math.min(usagePercent, 100)}%` }}
            />
          </div>
        </div>
      </section>

      {/* API Docs */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-primary" />
          <Typography variant="h3" className="text-xl font-semibold">
            API documentation
          </Typography>
        </div>
        <div className="rounded-lg border-2 border-input bg-card shadow-sm overflow-hidden">
          <div className="border-b border-input">
            <button
              onClick={() => setDocsSection(docsSection === "auth" ? null : "auth")}
              className="flex w-full items-center justify-between p-4 text-left hover:bg-muted/50 transition-colors"
            >
              <span className="flex items-center gap-2 font-medium">
                <Shield className="h-4 w-4 text-muted-foreground" />
                Authentication
              </span>
              {docsSection === "auth" ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </button>
            {docsSection === "auth" && (
              <div className="border-t border-input bg-muted/20 px-4 pb-4 pt-2">
                <p className="text-sm text-muted-foreground mb-3">
                  Include your API key in the request header:
                </p>
                <pre className="rounded-lg bg-foreground/5 p-4 text-sm overflow-x-auto border border-input">
                  <code>{`Authorization: Bearer YOUR_API_KEY`}</code>
                </pre>
                <p className="text-sm text-muted-foreground mt-3">
                  Or as a query parameter: <code className="rounded bg-muted px-1">?api_key=YOUR_API_KEY</code>
                </p>
              </div>
            )}
          </div>
          <div className="border-b border-input">
            <button
              onClick={() => setDocsSection(docsSection === "base" ? null : "base")}
              className="flex w-full items-center justify-between p-4 text-left hover:bg-muted/50 transition-colors"
            >
              <span className="flex items-center gap-2 font-medium">
                <Code2 className="h-4 w-4 text-muted-foreground" />
                Base URL
              </span>
              {docsSection === "base" ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </button>
            {docsSection === "base" && (
              <div className="border-t border-input bg-muted/20 px-4 pb-4 pt-2">
                <pre className="rounded-lg bg-foreground/5 p-4 text-sm overflow-x-auto border border-input">
                  <code>https://api.postpally.com/v1</code>
                </pre>
                <p className="text-sm text-muted-foreground mt-3">
                  Example cURL request:
                </p>
                <pre className="rounded-lg bg-foreground/5 p-4 text-sm overflow-x-auto border border-input mt-2">
                  <code>{`curl -X GET "https://api.postpally.com/v1/agents" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json"`}</code>
                </pre>
              </div>
            )}
          </div>
          <div>
            <button
              onClick={() => setDocsSection(docsSection === "endpoints" ? null : "endpoints")}
              className="flex w-full items-center justify-between p-4 text-left hover:bg-muted/50 transition-colors"
            >
              <span className="flex items-center gap-2 font-medium">
                <Code2 className="h-4 w-4 text-muted-foreground" />
                Endpoints
              </span>
              {docsSection === "endpoints" ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </button>
            {docsSection === "endpoints" && (
              <div className="border-t border-input bg-muted/20 px-4 pb-4 pt-2">
                <div className="space-y-2">
                  {DUMMY_ENDPOINTS.map((ep) => (
                    <div
                      key={`${ep.method}-${ep.path}`}
                      className="flex flex-wrap items-center gap-2 rounded-lg border border-input bg-background p-3"
                    >
                      <span
                        className={`rounded px-2 py-0.5 text-xs font-mono font-semibold ${
                          ep.method === "GET"
                            ? "bg-primary/20 text-primary"
                            : "bg-secondary/20 text-secondary"
                        }`}
                      >
                        {ep.method}
                      </span>
                      <code className="text-sm font-mono">{ep.path}</code>
                      <span className="text-sm text-muted-foreground">{ep.description}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};
